import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, computed, effect, forwardRef, inject, input, output, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkOverlayOrigin, CdkConnectedOverlay } from '@angular/cdk/overlay';
import { SearchableSelectCoordinatorService } from './searchable-select-coordinator.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-searchable-select',
    standalone: true,
    host: { class: 'block relative w-full min-w-0' },
    imports: [CommonModule, FormsModule, CdkOverlayOrigin, CdkConnectedOverlay],
    templateUrl: './searchable-select.component.html',
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SearchableSelectComponent), multi: true }],
})
export class SearchableSelectComponent implements ControlValueAccessor, OnDestroy {
    options = input<any[]>([]);
    bindLabel = input<string>('label');
    bindValue = input<string>('value');
    labelFn = input<((item: any) => string) | null>(null);
    multiple = input<boolean>(false);
    placeholder = input<string>('Select...');
    searchPlaceholder = input<string>('Search...');
    disabledInput = input<boolean>(false, { alias: 'disabled' });
    invalid = input<boolean>(false);
    clearable = input<boolean>(true);
    maxChips = input<number | null>(null);
    triggerClass = input<string>('');

    selectionChange = output<any>();

    private readonly coordinator = inject(SearchableSelectCoordinatorService);
    private readonly id = this.coordinator.nextId();
    private readonly formValue = signal<any[]>([]);
    private readonly formDisabled = signal(false);

    private onChange: (value: any) => void = () => {};
    private onTouched: () => void = () => {};

    protected readonly open = computed(() => this.coordinator.isOpen(this.id));
    protected readonly disabled = computed(() => this.formDisabled() || this.disabledInput());
    protected readonly searchTerm = signal('');
    private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
    private readonly trigger = viewChild<CdkOverlayOrigin>('trigger');
    private readonly connectedOverlay = viewChild(CdkConnectedOverlay);
    private resizeObserver: ResizeObserver | null = null;

    getRawValue(item: any): any {
        if (item == null) return null;
        if (typeof item !== 'object') return item;
        const key = this.bindValue();
        if (key && item[key] !== undefined) return item[key];
        if (item.value !== undefined) return item.value;
        if (item.id !== undefined) return item.id;
        if (item.code !== undefined) return item.code;
        return item;
    }

    getRawLabel(item: any): string {
        if (item == null) return '';
        const fn = this.labelFn();
        if (fn) return fn(item);
        if (typeof item !== 'object') return String(item);
        const key = this.bindLabel();
        if (key && item[key] !== undefined) return String(item[key]);
        if (item.label !== undefined) return String(item.label);
        if (item.name !== undefined) return String(item.name);
        if (item.title !== undefined) return String(item.title);
        if (item.role_name !== undefined) return String(item.role_name);
        if (item.designation_title !== undefined) return String(item.designation_title);
        return String(item);
    }

    protected readonly filteredOptions = computed(() => {
        const term = this.searchTerm().trim().toLowerCase();
        const opts = this.options() || [];
        if (!term) return opts;
        return opts.filter((option) => this.getRawLabel(option).toLowerCase().includes(term));
    });

    protected readonly selectedValues = computed(() => this.formValue());

    protected readonly selectedItemsWithLabels = computed(() => {
        const values = this.selectedValues();
        const opts = this.options() || [];
        return values.map((val) => {
            const found = opts.find((o) => {
                const optVal = this.getRawValue(o);
                return optVal == val || String(optVal) === String(val);
            });
            const label = found ? this.getRawLabel(found) : String(val);
            return { value: val, label };
        });
    });

    protected readonly visibleSelectedChips = computed(() => {
        const chips = this.selectedItemsWithLabels();
        const limit = this.maxChips();
        if (limit != null && limit > 0 && chips.length > limit) {
            return chips.slice(0, limit);
        }
        return chips;
    });

    protected readonly remainingChipsCount = computed(() => {
        const chips = this.selectedItemsWithLabels();
        const limit = this.maxChips();
        if (limit != null && limit > 0 && chips.length > limit) {
            return chips.length - limit;
        }
        return 0;
    });

    protected readonly displayText = computed(() => {
        const items = this.selectedItemsWithLabels();
        if (items.length === 0) return this.placeholder();
        return items[0].label;
    });

    protected readonly showChips = computed(() => this.multiple() && this.selectedValues().length > 0);

    private readonly onScrollCapture = (event: Event) => {
        const target = event.target as HTMLElement | null;
        if (target && target.closest('.max-h-56')) {
            return;
        }
        if (this.open()) {
            this.coordinator.close(this.id);
        }
    };

    constructor() {
        document.addEventListener('scroll', this.onScrollCapture, true);

        effect(() => {
            if (this.open()) {
                setTimeout(() => this.searchInput()?.nativeElement.focus());
            }
        });

        effect(() => {
            const trig = this.trigger();
            if (trig?.elementRef?.nativeElement && typeof ResizeObserver !== 'undefined') {
                this.resizeObserver?.disconnect();
                this.resizeObserver = new ResizeObserver(() => {
                    if (this.open()) {
                        this.connectedOverlay()?.overlayRef?.updatePosition();
                    }
                });
                this.resizeObserver.observe(trig.elementRef.nativeElement);
            }
        });
    }

    ngOnDestroy(): void {
        document.removeEventListener('scroll', this.onScrollCapture, true);
        this.resizeObserver?.disconnect();
    }

    writeValue(value: any): void {
        if (value == null || value === '') {
            this.formValue.set([]);
        } else if (Array.isArray(value)) {
            this.formValue.set(value);
        } else {
            this.formValue.set([value]);
        }
    }

    registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.formDisabled.set(isDisabled);
        if (isDisabled) this.coordinator.close(this.id);
    }

    toggle(): void {
        if (this.disabled()) return;
        this.coordinator.toggle(this.id);
        if (this.open()) {
            this.searchTerm.set('');
        }
    }

    isSelected(optionValue: any): boolean {
        return this.selectedValues().some((v) => v == optionValue || String(v) === String(optionValue));
    }

    private emitValue(next: any[]): void {
        this.formValue.set(next);
        const emitted = this.multiple() ? next : (next.length > 0 ? next[0] : null);
        this.onChange(emitted);
        this.selectionChange.emit(emitted);
        this.onTouched();
    }

    selectOption(option: any): void {
        if (this.disabled()) return;
        const val = this.getRawValue(option);
        if (this.multiple()) {
            const current = this.selectedValues();
            const exists = current.some((v) => v == val || String(v) === String(val));
            const next = exists
                ? current.filter((v) => !(v == val || String(v) === String(val)))
                : [...current, val];
            this.emitValue(next);
        } else {
            this.emitValue([val]);
            this.coordinator.close(this.id);
        }
    }

    clear(event: Event): void {
        event.stopPropagation();
        if (this.disabled()) return;
        this.emitValue([]);
    }

    removeSelected(val: any, event: Event): void {
        event.stopPropagation();
        if (this.disabled()) return;
        const next = this.selectedValues().filter((v) => !(v == val || String(v) === String(val)));
        this.emitValue(next);
    }

    selectAllFiltered(): void {
        if (this.disabled() || !this.multiple()) return;
        const filteredVals = this.filteredOptions()
            .filter((opt) => !opt.disabled)
            .map((opt) => this.getRawValue(opt));
        const current = this.selectedValues();
        const additions = filteredVals.filter((f) => !current.some((c) => c == f || String(c) === String(f)));
        const next = [...current, ...additions];
        this.emitValue(next);
    }

    unselectAll(): void {
        if (this.disabled() || !this.multiple()) return;
        this.emitValue([]);
    }

    @HostListener('document:click')
    closePanel(): void {
        this.coordinator.close(this.id);
    }
}
